import type { MediaRef } from '../components/media/SmartImage';

export const SITE_URL = 'https://telesafi.ma';
export const SITE_NAME = 'تلي صافي';
export const SITE_NAME_LATIN = 'Telesafi';
export const SITE_DESCRIPTION = 'أخبار المغرب والعالم — تلي صافي';
export const SITE_LOCALE = 'ar_MA';

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/** Newsroom contact surfaced in `contactPoint` and on the about page. */
export const EDITORIAL_EMAIL = 'editorial@telesafi.ma';

/**
 * Official profiles, in one place.
 *
 * They feed three things at once: the header/footer icon rows, the follow box
 * on articles, and `sameAs` on the organization node — the signal Google uses
 * to tie telesafi.ma to the accounts publishing under the same name. Entries
 * with an empty `url` render nothing and are left out of `sameAs`: a dead
 * `href="#"` is a link to nowhere on every page of the site, and a fabricated
 * profile URL in `sameAs` is worse than none.
 *
 * TODO(editorial): fill in the real profile URLs.
 */
export type SocialLink = { name: string; url: string; icon: string; label: string };

export const SOCIAL_LINKS: ReadonlyArray<SocialLink> = (
  [
    { name: 'Facebook', url: '', icon: 'fab fa-facebook-f', label: 'فيسبوك' },
    { name: 'X', url: '', icon: 'fab fa-twitter', label: 'إكس' },
    { name: 'YouTube', url: '', icon: 'fab fa-youtube', label: 'يوتيوب' },
    { name: 'Instagram', url: '', icon: 'fab fa-instagram', label: 'إنستغرام' },
  ] satisfies SocialLink[]
).filter((s) => s.url.length > 0);

export const SAME_AS: ReadonlyArray<string> = SOCIAL_LINKS.map((s) => s.url);

/** Publisher logo. Must be a real, crawlable file — Google drops the whole
 *  NewsArticle rich result when `publisher.logo` 404s. */
export const LOGO = {
  url: `${SITE_URL}/assets/images/logos/telesafi-logo-original.png`,
  width: 500,
  height: 500,
};

/**
 * Display copy of the logo for the header and footer.
 *
 * The 500x500 PNG above stays where it is — it is the file named in
 * `publisher.logo`, and Google fetches it. But it is rendered at 85 CSS px,
 * and as the first eager image on every page React hoists it into a
 * high-priority `<link rel="preload" as="image">`: 40KB of the LCP budget
 * spent on a logo. This is the same mark at 2x display size, 13KB.
 */
export const LOGO_DISPLAY = {
  url: '/assets/images/logos/telesafi-logo-200.webp',
  width: 200,
  height: 200,
};

/** 1200x630 branded card used whenever an article has no usable image. */
export const DEFAULT_OG = {
  url: `${SITE_URL}/assets/images/og-default.jpg`,
  width: 1200,
  height: 630,
};

export function absoluteUrl(pathname: string): string {
  if (/^https?:\/\//i.test(pathname)) return pathname;
  return `${SITE_URL}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
}

/** Percent-encodes each path segment while keeping `/` separators, so Arabic
 *  slugs in canonicals are byte-identical to the `<loc>` values in sitemap.xml. */
export function encodePath(slug: string): string {
  return slug.split('/').filter(Boolean).map(encodeURIComponent).join('/');
}

export function articleUrl(slug: string): string {
  return `${SITE_URL}/articles/${encodeURIComponent(slug)}`;
}

export function categoryUrl(slug: string, page = 1): string {
  const base = `${SITE_URL}/category/${encodePath(slug)}`;
  return page > 1 ? `${base}?page=${page}` : base;
}

/** Shareable short link for an article, or the canonical URL when the article
 *  predates short codes. */
export function shortUrl(shortCode: string | null | undefined, slug: string): string {
  return shortCode ? `${SITE_URL}/s/${shortCode}` : articleUrl(slug);
}

export type OgImage = { url: string; width?: number; height?: number; alt?: string };

/**
 * Largest original we will hand a scraper when no 1200x630 crop exists.
 * WhatsApp gives up on link-preview images around 600KB and Facebook times out
 * on multi-megabyte PNGs, and a preview that fails to fetch renders as no image
 * at all — worse than the branded card.
 */
const MAX_UNCROPPED_OG_BYTES = 1_000_000;

/**
 * Resolves the social card image for an article.
 *
 * Order: explicit OG media → its 1200x630 crop → cover media crop → cover
 * original → branded default. `media.path` is never used: it is a storage key,
 * and a relative og:image is silently dropped by every scraper.
 */
export function ogImageFor(opts: {
  ogImageMedia?: MediaRef;
  coverMedia?: MediaRef;
  alt?: string;
}): OgImage {
  const candidates: MediaRef[] = [opts.ogImageMedia ?? null, opts.coverMedia ?? null];
  for (const media of candidates) {
    if (!media) continue;
    if (media.ogUrl) {
      return {
        url: media.ogUrl,
        width: media.ogWidth ?? 1200,
        height: media.ogHeight ?? 630,
        alt: opts.alt,
      };
    }
    if (media.url && /^https?:\/\//i.test(media.url)) {
      if (media.size != null && media.size > MAX_UNCROPPED_OG_BYTES) continue;
      return {
        url: media.url,
        // Only what we actually know. Declaring a size the file does not have
        // makes scrapers lay out the wrong box or drop the card entirely.
        width: media.width ?? undefined,
        height: media.height ?? undefined,
        alt: opts.alt,
      };
    }
  }
  return { ...DEFAULT_OG, alt: opts.alt };
}

/** og:image + twitter:image meta tags for a resolved social card. */
export function ogImageMeta(image: OgImage) {
  return [
    { property: 'og:image', content: image.url },
    ...(image.width ? [{ property: 'og:image:width', content: String(image.width) }] : []),
    ...(image.height ? [{ property: 'og:image:height', content: String(image.height) }] : []),
    ...(image.alt ? [{ property: 'og:image:alt', content: image.alt }] : []),
    { name: 'twitter:image', content: image.url },
    ...(image.alt ? [{ name: 'twitter:image:alt', content: image.alt }] : []),
  ];
}

/**
 * ISO 8601 timestamp for `article:published_time` / `article:modified_time`.
 *
 * The API client deserializes these into `Date` objects, and interpolating one
 * into a meta tag yields `Sat Aug 01 2026 22:52:59 GMT+0100 (…)`, which no
 * consumer parses. JSON-LD is unaffected because `JSON.stringify` already
 * emits ISO for Date values.
 */
export function isoDate(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function stripHtml(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Trims to a whole word so descriptions never end mid-token. */
export function clamp(text: string, max: number): string {
  const clean = stripHtml(text);
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

export function canonical(pathname: string): { rel: 'canonical'; href: string } {
  return { rel: 'canonical', href: absoluteUrl(pathname) };
}

export function staticHead(opts: {
  pathname: string;
  title: string;
  description?: string | null;
}) {
  const fullTitle = `${opts.title} - ${SITE_NAME}`;
  const url = absoluteUrl(opts.pathname);
  const image = DEFAULT_OG;
  return {
    meta: [
      { title: fullTitle },
      ...(opts.description
        ? [
            { name: 'description', content: opts.description },
            { property: 'og:description', content: opts.description },
            { name: 'twitter:description', content: opts.description },
          ]
        : []),
      { property: 'og:title', content: fullTitle },
      { property: 'og:url', content: url },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:title', content: fullTitle },
      ...ogImageMeta({ ...image, alt: SITE_NAME }),
    ],
    links: [{ rel: 'canonical', href: url }],
  };
}
